import { CartDrawer, useCartDrawer } from '@iheartjane/cart';
import { Hide, useMobileMediaQuery } from '@iheartjane/reefer';
import { useEffect, useRef, useState } from 'react';

import { CartAlerts } from '@jane/monolith/components/cartAlerts';
import {
  useCustomerDispatch,
  useCustomerSelector,
} from '@jane/monolith/customer/redux';
import { selectCart } from '@jane/monolith/customer/redux/cart';
import { openModal } from '@jane/monolith/customer/redux/modal';
import { useBrandSpecialsContext } from '@jane/shared-ecomm/providers';
import { useEcommApp } from '@jane/shared/providers';

import { CartDrawerContainer } from './cartDrawer/cartDrawerContainer';

export const MarketplaceCartDrawer = () => {
  const [showCartAlerts, setShowCartAlerts] = useState<boolean>(false);
  const isMobile = useMobileMediaQuery({});
  const dispatch = useCustomerDispatch();
  const { isEmbed } = useEcommApp();
  const { isOpen: isCartOpen } = useCartDrawer();
  const {
    cart,
    isRemovingSpecialHasChangedFlag,
    isDeletingUnavailableProducts,
  } = useCustomerSelector(selectCart);
  const { showBrandSpecialAlert } = useBrandSpecialsContext();

  const [cartMessage, setCartMessage] = useState<string>();

  const {
    brand_special_unavailable_reason,
    special_has_changed,
    unavailable_products,
    removed_crm_redemptions,
    products,
  } = cart;
  const prevProducts = useRef(products || []);

  const cartHasChanged =
    showBrandSpecialAlert ||
    !!brand_special_unavailable_reason ||
    (special_has_changed && !isRemovingSpecialHasChangedFlag) ||
    (unavailable_products &&
      unavailable_products.length &&
      !isDeletingUnavailableProducts) ||
    (removed_crm_redemptions && removed_crm_redemptions.length > 0);

  useEffect(() => {
    /**
     * We don't want to trigger openModal on subdomains so we need
     * to also check if we are on embeddedMode
     */
    if (isCartOpen && cartHasChanged && !isEmbed) {
      dispatch(
        openModal({
          name: 'cartAlerts',
        })
      );
    }
  }, [isCartOpen, cartHasChanged]);

  useEffect(() => {
    const currentProductLength = products?.length || 0;
    const productAdded = currentProductLength > prevProducts.current.length;
    const productRemoved = prevProducts.current.length > currentProductLength;

    if (productAdded && isCartOpen) {
      setCartMessage(
        `${products[products.length - 1].name} added to your bag.`
      );
    } else if (productRemoved && isCartOpen) {
      const removedProductName = prevProducts.current.find(
        (prevProduct) =>
          !products?.some((product) => product.id === prevProduct.id)
      )?.name;
      setCartMessage(
        `${removedProductName ?? 'product'} removed from your bag.`
      );
    } else if (!isCartOpen) {
      setCartMessage('');
    }
    prevProducts.current = products || [];
  }, [products, isCartOpen]);

  return (
    <CartDrawer isMobile={isMobile}>
      <Hide isHidden aria-live="polite" aria-atomic="true">
        {cartMessage}
      </Hide>
      <CartDrawerContainer setShowCartAlerts={setShowCartAlerts} />
      {isEmbed && showCartAlerts && (
        <CartAlerts isCheckoutAlert onClose={() => setShowCartAlerts(false)} />
      )}
    </CartDrawer>
  );
};
